import React from 'react';
import { Star, User } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Avatar } from '../ui/avatar';

export const ProductReviews = ({ productId, rating, reviewsCount }) => {
  // Use props or provide defaults
  const displayRating = rating || 4.7;
  const displayReviewsCount = reviewsCount || 0;
  // Mock reviews data - in production, fetch from API
  const mockReviews = [
    {
      id: 1,
      author: 'Sarah M.',
      rating: 5,
      date: '2 weeks ago',
      comment: 'Absolutely love this product! My skin feels so much softer and more hydrated. Highly recommend!',
      verified: true
    },
    {
      id: 2,
      author: 'Jennifer K.',
      rating: 5,
      date: '1 month ago',
      comment: 'Amazing quality! I\'ve been using this for a month now and can see visible improvements.',
      verified: true
    },
    {
      id: 3,
      author: 'Emily R.',
      rating: 4,
      date: '1 month ago',
      comment: 'Very good product. Works as described. The only downside is the price, but worth it for the quality.',
      verified: false
    }
  ];

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`w-4 h-4 ${
              i < rating ? 'fill-primary text-primary' : 'text-muted-foreground'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Rating Summary */}
      <div className="flex items-center gap-8 p-6 bg-muted/30 rounded-lg">
        <div className="text-center">
          <div className="text-5xl font-bold text-primary mb-2">{displayRating}</div>
          <div className="flex items-center gap-1 mb-1">
            {renderStars(Math.round(displayRating))}
          </div>
          <p className="text-sm text-muted-foreground">{displayReviewsCount} reviews</p>
        </div>
        <div className="flex-1">
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map(stars => {
              const percentage = stars === 5 ? 75 : stars === 4 ? 20 : 5;
              return (
                <div key={stars} className="flex items-center gap-3">
                  <div className="flex items-center gap-1 w-20">
                    <span className="text-sm">{stars}</span>
                    <Star className="w-3 h-3 fill-primary text-primary" />
                  </div>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-muted-foreground w-12">{percentage}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        <h3 className="font-serif text-2xl font-semibold">Customer Reviews</h3>
        {mockReviews.map(review => (
          <Card key={review.id}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-semibold">
                        {review.author}
                        {review.verified && (
                          <span className="ml-2 text-xs text-success">✓ Verified Purchase</span>
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">{review.date}</p>
                    </div>
                    {renderStars(review.rating)}
                  </div>
                  <p className="text-muted-foreground leading-relaxed">
                    {review.comment}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};